import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { Patient, PatientGender, PatientInput, PatientType, PatientCoverageType } from "@/core/patients/types";
import {
  PATIENT_GENDER_LABELS,
  PATIENT_TYPE_LABELS,
  PATIENT_COVERAGE_LABELS,
} from "@/core/patients/types";
import { listInsurers, listInsurerAgents } from "@/core/insurance/insurer-store";
import { insurerAgentFullName } from "@/core/insurance/types";

interface PatientFormModalProps {
  open: boolean;
  patient?: Patient | null;
  onClose: () => void;
  onSave: (input: PatientInput) => void;
}

const emptyForm: PatientInput = {
  firstName: "",
  lastName: "",
  gender: "male",
  patientType: "outpatient",
  coverageType: "none",
  coverageName: "",
  insurerId: null,
  insurerAgentId: null,
  memberNumber: "",
  policyNumber: "",
  phone: "",
  address: "",
  birthDate: "",
  notes: "",
};

export function PatientFormModal({ open, patient, onClose, onSave }: PatientFormModalProps) {
  const [form, setForm] = useState<PatientInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const firstRef = useRef<HTMLInputElement>(null);

  const insurers = useMemo(() => listInsurers(true), [open]);
  const agents = useMemo(
    () => (form.insurerId ? listInsurerAgents(form.insurerId).filter((a) => a.active) : []),
    [form.insurerId, open],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (patient) {
      setForm({
        firstName: patient.firstName,
        lastName: patient.lastName,
        gender: patient.gender,
        patientType: patient.patientType,
        coverageType: patient.coverageType,
        coverageName: patient.coverageName,
        insurerId: patient.insurerId,
        insurerAgentId: patient.insurerAgentId,
        memberNumber: patient.memberNumber,
        policyNumber: patient.policyNumber,
        phone: patient.phone,
        address: patient.address,
        birthDate: patient.birthDate,
        notes: patient.notes,
      });
    } else {
      setForm(emptyForm);
    }
    window.setTimeout(() => firstRef.current?.focus(), 50);
  }, [open, patient]);

  if (!open) return null;

  const isInsurer = (form.coverageType ?? "none") === "insurer";
  const isCompany = (form.coverageType ?? "none") === "company";

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Nom et prénom requis.");
      return;
    }
    if (isInsurer && !form.insurerId) {
      setError("Sélectionnez l'assureur (client CRM).");
      return;
    }
    onSave(form);
    onClose();
  };

  const handleInsurerChange = (insurerId: string) => {
    const insurer = insurers.find((i) => i.id === insurerId);
    setForm((f) => ({
      ...f,
      insurerId: insurerId || null,
      insurerAgentId: null,
      coverageName: insurer?.name ?? f.coverageName,
    }));
  };

  return (
    <div className="ph-modal-backdrop" onClick={onClose}>
      <div className="ph-modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="h5 mb-3">{patient ? "Modifier le patient" : "Nouveau patient"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label small" htmlFor="pat-last-name">
                Nom *
              </label>
              <input
                ref={firstRef}
                id="pat-last-name"
                className="form-control form-control-sm"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                required
              />
            </div>
            <div className="col-6">
              <label className="form-label small" htmlFor="pat-first-name">
                Prénom *
              </label>
              <input
                id="pat-first-name"
                className="form-control form-control-sm"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label small" htmlFor="pat-gender">
                Genre *
              </label>
              <select
                id="pat-gender"
                className="form-select form-select-sm"
                value={form.gender}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gender: e.target.value as PatientGender }))
                }
                required
              >
                {(Object.keys(PATIENT_GENDER_LABELS) as PatientGender[]).map((key) => (
                  <option key={key} value={key}>
                    {PATIENT_GENDER_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label small" htmlFor="pat-type">
                Type patient *
              </label>
              <select
                id="pat-type"
                className="form-select form-select-sm"
                value={form.patientType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, patientType: e.target.value as PatientType }))
                }
                required
              >
                {(Object.keys(PATIENT_TYPE_LABELS) as PatientType[]).map((key) => (
                  <option key={key} value={key}>
                    {PATIENT_TYPE_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-12">
              <label className="form-label small" htmlFor="pat-coverage">
                Prise en charge
              </label>
              <select
                id="pat-coverage"
                className="form-select form-select-sm"
                value={form.coverageType ?? "none"}
                onChange={(e) => {
                  const coverageType = e.target.value as PatientCoverageType;
                  setForm((f) => ({
                    ...f,
                    coverageType,
                    coverageName: coverageType === "none" ? "" : f.coverageName,
                    insurerId: coverageType === "insurer" ? f.insurerId : null,
                    insurerAgentId: coverageType === "insurer" ? f.insurerAgentId : null,
                  }));
                }}
              >
                {(Object.keys(PATIENT_COVERAGE_LABELS) as PatientCoverageType[]).map((key) => (
                  <option key={key} value={key}>
                    {PATIENT_COVERAGE_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isInsurer && (
            <>
              <div className="mb-2">
                <label className="form-label small" htmlFor="pat-insurer">
                  Assureur (client CRM) *
                </label>
                <select
                  id="pat-insurer"
                  className="form-select form-select-sm"
                  value={form.insurerId ?? ""}
                  onChange={(e) => handleInsurerChange(e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {insurers.map((ins) => (
                    <option key={ins.id} value={ins.id}>
                      {ins.name} ({ins.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label small" htmlFor="pat-agent">
                  Agent en charge
                </label>
                <select
                  id="pat-agent"
                  className="form-select form-select-sm"
                  value={form.insurerAgentId ?? ""}
                  disabled={!form.insurerId}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      insurerAgentId: e.target.value || null,
                    }))
                  }
                >
                  <option value="">— Optionnel —</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.ref} — {insurerAgentFullName(agent)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="form-label small" htmlFor="pat-member">
                    N° adhérent
                  </label>
                  <input
                    id="pat-member"
                    className="form-control form-control-sm"
                    value={form.memberNumber ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, memberNumber: e.target.value }))}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small" htmlFor="pat-policy">
                    N° police
                  </label>
                  <input
                    id="pat-policy"
                    className="form-control form-control-sm"
                    value={form.policyNumber ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, policyNumber: e.target.value }))}
                  />
                </div>
              </div>
            </>
          )}

          {isCompany && (
            <div className="mb-2">
              <label className="form-label small" htmlFor="pat-coverage-name">
                Entreprise
              </label>
              <input
                id="pat-coverage-name"
                className="form-control form-control-sm"
                placeholder="Nom entreprise"
                value={form.coverageName ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, coverageName: e.target.value }))}
              />
            </div>
          )}

          <div className="mb-2">
            <label className="form-label small" htmlFor="pat-phone">
              Téléphone
            </label>
            <input
              id="pat-phone"
              className="form-control form-control-sm"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small" htmlFor="pat-address">
              Adresse
            </label>
            <textarea
              id="pat-address"
              className="form-control form-control-sm"
              rows={2}
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small" htmlFor="pat-birth">
              Date de naissance
            </label>
            <input
              id="pat-birth"
              type="date"
              className="form-control form-control-sm"
              value={form.birthDate ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, birthDate: e.target.value }))}
            />
          </div>
          <div className="mb-2">
            <label className="form-label small" htmlFor="pat-notes">
              Notes
            </label>
            <textarea
              id="pat-notes"
              className="form-control form-control-sm"
              rows={2}
              value={form.notes ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary btn-sm">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
